import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, cn } from '@teach/ui'
import type { Lesson } from '@/types/lesson'
import type { ContentLang } from '@/features/editor/editorSlice'
import {
  withLessonResult,
  type BlockIssue,
  type CourseFieldIssue,
  type LessonFieldIssue,
} from '@/features/editor/lib/publishIssues'
import { blockingIssues } from '@/features/editor/lib/publishOverflowIssues'
import { PublishIssuesTable } from './PublishIssuesTable'
import { LessonIssuesProbe } from './LessonIssuesProbe'
import { useGraceHover } from './useGraceHover'
import { useMessages } from '@/i18n'
import { messages } from './messages'

type LessonRef = Omit<Lesson, 'blocks'>

interface Props {
  lessons: LessonRef[]
  /** Что не заполнено у самого курса (известно без запросов). */
  fieldIssues: CourseFieldIssue[]
  /** Незаполненные названия и описания уроков. */
  lessonIssues: LessonFieldIssue[]
  /** Языки, на которых курс обязан быть заполнен (`requiredLangs`): экраны
   *  проверяются на каждом — вопрос, варианты, картинки. */
  langs: ContentLang[]
  children: ReactNode
  /**
   * Проверить экраны уроков СРАЗУ, а не по наведению, и сообщить итог в
   * `onBlocked`: кнопке «Обновить» в окне публикации надо знать заранее, гаснуть
   * ли ей. Окно открывают осознанно — запрос на урок здесь не лишний.
   */
  eager?: boolean
  /** Есть что заполнить (или проверка ещё идёт) — кнопку под облачком гасят. */
  onBlocked?: (blocked: boolean) => void
  /** Класс обёртки-триггера: в окне публикации кнопка на всю ширину. */
  className?: string
  /** Облачко не открывать вовсе: окно публикации уже открыто и само говорит,
   *  что не так, — белое облачко поверх него только закрывало бы окно. */
  suppressed?: boolean
}

/**
 * Облачко у «Опубликовать»: списком всё, что мешает публикации, — поля курса и
 * экраны уроков (пустое видео, квиз без правильного ответа). Экраны уроков
 * грузятся только при наведении: у большого курса это запрос на каждый урок.
 * Пусто — облачка нет вовсе.
 */
export const PublishIssuesTooltip = ({
  lessons,
  fieldIssues,
  lessonIssues,
  langs,
  children,
  eager = false,
  onBlocked,
  className,
  suppressed = false,
}: Props) => {
  const m = useMessages(messages)
  const { hovered, enter, leave, toggle, close } = useGraceHover()
  const triggerRef = useRef<HTMLSpanElement>(null)
  const [byLesson, setByLesson] = useState<Record<number, BlockIssue[]>>({})
  const probing = eager || hovered
  const loading = probing && lessons.some((l) => byLesson[l.id] === undefined)
  const blockIssues = lessons.flatMap((l) => byLesson[l.id] ?? [])
  const hasIssues = fieldIssues.length > 0 || lessonIssues.length > 0 || blockIssues.length > 0
  const open = hovered && !suppressed && (hasIssues || loading)
  // Рекомендации (жёлтое переполнение) облачко открывают, но кнопку не гасят.
  const blocked =
    loading || [fieldIssues, lessonIssues, blockingIssues(blockIssues)].some((l) => l.length > 0)
  useEffect(() => {
    onBlocked?.(blocked)
  }, [blocked, onBlocked])

  return (
    <TooltipProvider delayDuration={100}>
      {/* Наведение — по входу/уходу указателя, а не по `onOpenChange`: пока
          замечаний нет, облачко закрыто, и Radix не сообщает об уходе мыши —
          «наведено» залипало, и облачко само открывалось, как только появлялось
          замечание (добавили блок). Закрытия от Radix (`onOpenChange(false)`)
          не слушаем вовсе: он гасил облачко, пока мышь ещё на кнопке. */}
      <Tooltip open={open}>
        {/* У погашенной кнопки нет событий наведения — облачко вешаем на обёртку. */}
        <TooltipTrigger asChild>
          <span
            ref={triggerRef}
            tabIndex={0}
            // Погашенная кнопка внутри не получает событий указателя, и Chrome
            // при заходе на неё шлёт обёртке «ушли» — облачко мигало и гасло.
            // Без событий у самой погашенной кнопки указатель остаётся на обёртке.
            className={cn(className ?? 'inline-flex', '[&_button:disabled]:pointer-events-none')}
            onPointerEnter={enter}
            onPointerLeave={leave}
            // Касание (телефон): открыть/закрыть — только когда кнопка погашена (`blocked`),
            // иначе «открыто» залипло бы и облачко всплыло бы само при первом замечании.
            // Рекомендации кнопку не гасят: тап по активной кнопке просто действует,
            // а облачко после закрытия окна публикации не должно всплывать само.
            onPointerDown={(e) => {
              if (e.pointerType === 'touch' && blocked) toggle()
            }}
          >
            {children}
          </span>
        </TooltipTrigger>
        {probing
          ? lessons.map((lesson, rank) => (
              <LessonIssuesProbe
                key={lesson.id}
                lesson={lesson}
                rank={rank}
                langs={langs}
                onResult={(id, list) => setByLesson((prev) => withLessonResult(prev, id, list))}
              />
            ))
          : null}
        <TooltipContent
          onEscapeKeyDown={close}
          // Касание мимо закрывает; по самой кнопке — нет, её касание и так переключает.
          onPointerDownOutside={(e) => {
            if (!triggerRef.current?.contains(e.target as Node)) close()
          }}
          onPointerEnter={enter}
          onPointerLeave={leave}
          // Длинный список прокручивается внутри облачка, а не уезжает за экран.
          className="max-h-[min(70vh,var(--radix-tooltip-content-available-height))] max-w-md overflow-y-auto p-3"
        >
          {hasIssues ? (
            <PublishIssuesTable fields={fieldIssues} lessons={lessonIssues} blocks={blockIssues} />
          ) : null}
          {loading ? <p className="mt-2 text-xs opacity-70">{m.checkingLessons}</p> : null}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
